import React, { useRef, useState } from 'react'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Button, CloseIcon, colors, DragIcon, TextField, Tooltip } from '@diversioteam/diversio-ds'
import { nanoid } from 'nanoid'
import { CheckBoxOutlineBlank } from '@material-ui/icons'

import { FieldWrapper } from './../FieldWrapper'
import { focusElement } from './../../formBuilder.helpers'
import { CheckboxInactive } from './checkboxInactive'
import { CheckboxProps } from './checkbox.types'
import * as S from './../fields.styles'

export const Checkbox = ({
    item,
    isActive,
    isDeleteElementButtonDisabled,
    onAddElement,
    onChangeElementValue,
    onDeleteElement,
    onToggleRequired,
    onChangeElementType,
    onAddOption,
    onChangeOptionValue,
    onDeleteOption,
    onReorderOptions,
    onDuplicateElement,
    onChangeActiveElement,
    onToggleOtherOption,
    previewMode,
}: CheckboxProps) => {
    const [hasOther, setHasOther] = useState(item.hasOther || false)

    const optionRefs = useRef<{ [key: string]: HTMLInputElement | null }>({})

    const isDeleteOptionButtonDisabled = item.options.length === 1

    const createNewOption = (id: string) => {
        const newOptionId = nanoid()
        const data = {
            id: newOptionId,
            value: 'Option',
        }

        onAddOption(id, data)

        // Focus on the newly added option
        setTimeout(() => {
            optionRefs.current[newOptionId]?.focus()
        }, 0)
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event

        if (!over) {
            return
        }

        if (active.id !== over.id) {
            const oldIndex = item.options.findIndex((option) => option.id === active.id)
            const newIndex = item.options.findIndex((option) => option.id === over.id)

            const reorderedOptions = arrayMove(item.options, oldIndex, newIndex)

            onReorderOptions(item.id, reorderedOptions)
        }
    }

    const handleAddOtherOption = () => {
        setHasOther(true)

        onToggleOtherOption(item.id, true)
    }

    const handleDeleteOtherOption = () => {
        setHasOther(false)

        onToggleOtherOption(item.id, false)
    }

    if (previewMode) {
        return <CheckboxInactive item={item} previewMode={previewMode} />
    }

    if (!isActive) {
        return <CheckboxInactive item={item} onClick={() => onChangeActiveElement(item.id)} />
    }

    return (
        <FieldWrapper
            item={item}
            isActive={isActive}
            isDeleteElementButtonDisabled={isDeleteElementButtonDisabled}
            onAddElement={onAddElement}
            onChangeElementValue={onChangeElementValue}
            onDeleteElement={onDeleteElement}
            onToggleRequired={onToggleRequired}
            onChangeElementType={onChangeElementType}
            onDuplicateElement={onDuplicateElement}
            onChangeActiveElement={onChangeActiveElement}
        >
            <S.OptionList>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={item.options} strategy={verticalListSortingStrategy}>
                        {item.options.map((option, index) => (
                            <S.SortableItemWrapper
                                key={option.id}
                                id={option.id}
                                dragHandle={
                                    <S.OptionDragIcon>
                                        <DragIcon type="line" color={colors.primary40} />
                                    </S.OptionDragIcon>
                                }
                            >
                                <S.OptionRow>
                                    <CheckBoxOutlineBlank />

                                    <TextField
                                        name={`option-${item.id}-${index + 1}`}
                                        aria-label={`Option ${index + 1}`}
                                        placeholder={`Option ${index + 1}`}
                                        defaultValue={option?.value}
                                        key={option?.id}
                                        onBlur={(e) => onChangeOptionValue(item?.id, option?.id, e.target.value)}
                                        onFocus={focusElement}
                                        variant="standard"
                                        fullWidth
                                        multiline
                                        inputRef={(el) => (optionRefs.current[option.id] = el)}
                                    />

                                    <Tooltip title="Delete Option">
                                        <Button
                                            aria-label="Delete Option"
                                            disabled={isDeleteOptionButtonDisabled}
                                            onClick={() => onDeleteOption(item.id, option?.id)}
                                            color="transparent"
                                            iconOnly
                                        >
                                            <CloseIcon type="line" />
                                        </Button>
                                    </Tooltip>
                                </S.OptionRow>
                            </S.SortableItemWrapper>
                        ))}
                    </SortableContext>
                </DndContext>

                {hasOther && (
                    <S.OptionRow>
                        <CheckBoxOutlineBlank />

                        <TextField
                            name={`other-${item.id}`}
                            aria-label="Other"
                            placeholder="Other"
                            defaultValue="Other"
                            onBlur={(e) => onChangeOptionValue(item.id, 'other', e.target.value)}
                            variant="standard"
                            fullWidth
                            disabled
                        />

                        <Tooltip title="Delete Option">
                            <Button
                                aria-label="Delete Option"
                                onClick={handleDeleteOtherOption}
                                color="transparent"
                                iconOnly
                            >
                                <CloseIcon type="line" />
                            </Button>
                        </Tooltip>
                    </S.OptionRow>
                )}

                <S.AddOptionRow>
                    <CheckBoxOutlineBlank />

                    <TextField
                        name={`add-option-${item.id}`}
                        aria-label="Add option"
                        defaultValue="Add option"
                        onClick={() => createNewOption(item.id)}
                        variant="standard"
                    />

                    {!hasOther && (
                        <>
                            <span>or</span>

                            <Button aria-label="Add Other" onClick={handleAddOtherOption} color="secondaryLight">
                                Add ‘Other’
                            </Button>
                        </>
                    )}
                </S.AddOptionRow>
            </S.OptionList>
        </FieldWrapper>
    )
}
